// PerformanceMonitoringSystem.js
// Enterprise-grade performance monitoring and optimization
// Provides real-time performance insights and automatic optimizations

import { MobileDetectionService } from './MobileDetectionService.js';
import { professionalNotifications } from './ProfessionalNotificationSystem.js';

/**
 * Performance monitoring system
 * Monitors scene transitions, memory usage, and user interactions
 * Provides automatic optimizations and performance alerts
 */
export class PerformanceMonitoringSystem {
    static _instance = null;
    
    /**
     * Singleton pattern for consistent performance monitoring
     */
    static getInstance() {
        if (!PerformanceMonitoringSystem._instance) {
            PerformanceMonitoringSystem._instance = new PerformanceMonitoringSystem();
        }
        return PerformanceMonitoringSystem._instance;
    }
    
    constructor() {
        if (PerformanceMonitoringSystem._instance) {
            return PerformanceMonitoringSystem._instance;
        }
        
        this.metrics = {
            sceneTransitions: [],
            memoryUsage: [],
            userInteractions: [],
            loadTimes: [],
            errors: []
        };
        
        this.thresholds = {
            slowTransition: 1000, // ms
            highMemoryUsage: 200, // MB (increased for game scenes)
            errorRate: 0.05, // 5%
            slowLoad: 3000 // ms
        };
        
        this.isMonitoring = false;
        this.monitoringInterval = null;
        
        this._initializeMonitoring();
        
        PerformanceMonitoringSystem._instance = this;
    }
    
    /**
     * Start performance monitoring
     */
    startMonitoring() {
        if (this.isMonitoring) return;
        
        console.log('📊 PerformanceMonitoringSystem: Starting enterprise monitoring');
        
        this.isMonitoring = true;
        
        // Monitor memory usage every 30 seconds
        this.monitoringInterval = setInterval(() => {
            this._collectMemoryMetrics();
            this._analyzePerformanceTrends();
        }, 30000);
        
        // Monitor user interactions
        this._setupInteractionMonitoring();
        
        // Monitor scene transitions (integrated with NavigationActionHandler)
        this._setupTransitionMonitoring();
        
        console.log('✅ Performance monitoring active');
    }
    
    /**
     * Stop performance monitoring
     */
    stopMonitoring() {
        if (!this.isMonitoring) return;
        
        console.log('📊 PerformanceMonitoringSystem: Stopping monitoring');
        
        this.isMonitoring = false;
        
        if (this.monitoringInterval) {
            clearInterval(this.monitoringInterval);
            this.monitoringInterval = null;
        }
        
        console.log('✅ Performance monitoring stopped');
    }
    
    /**
     * Record scene transition performance
     * @param {string} fromScene - Source scene
     * @param {string} toScene - Target scene
     * @param {number} duration - Transition duration in ms
     * @param {boolean} success - Whether transition was successful
     */
    recordTransition(fromScene, toScene, duration, success = true) {
        const transition = {
            fromScene,
            toScene,
            duration,
            success,
            timestamp: Date.now(),
            isMobile: MobileDetectionService.isMobile()
        };
        
        this.metrics.sceneTransitions.push(transition);
        
        // Keep only last 100 transitions
        if (this.metrics.sceneTransitions.length > 100) {
            this.metrics.sceneTransitions.shift();
        }
        
        // Check for performance issues
        if (duration > this.thresholds.slowTransition) {
            console.warn(`⚠️ Slow transition detected: ${fromScene} → ${toScene} (${duration}ms)`);
            
            // Show warning for very slow transitions
            if (duration > 2000) {
                professionalNotifications.warning(
                    `Slow navigation detected. Optimizing performance...`,
                    { duration: 3000 }
                );
            }
        }
        
        console.log(`📊 Transition recorded: ${fromScene} → ${toScene} (${duration}ms)`);
    }
    
    /**
     * Record user interaction performance
     * @param {string} action - Action type
     * @param {number} responseTime - Response time in ms
     * @param {string} context - Interaction context
     */
    recordInteraction(action, responseTime, context = '') {
        const interaction = {
            action,
            responseTime,
            context,
            timestamp: Date.now(),
            isMobile: MobileDetectionService.isMobile()
        };
        
        this.metrics.userInteractions.push(interaction);
        
        // Keep only last 200 interactions
        if (this.metrics.userInteractions.length > 200) {
            this.metrics.userInteractions.shift();
        }
        
        // Check for slow interactions
        if (responseTime > 500) {
            console.warn(`⚠️ Slow interaction: ${action} (${responseTime}ms)`);
        }
    }
    
    /**
     * Record load time performance
     * @param {string} resource - Resource name
     * @param {number} loadTime - Load time in ms
     * @param {boolean} success - Whether load was successful
     */
    recordLoadTime(resource, loadTime, success = true) {
        const load = {
            resource,
            loadTime,
            success,
            timestamp: Date.now(),
            isMobile: MobileDetectionService.isMobile()
        };
        
        this.metrics.loadTimes.push(load);
        
        // Keep only last 50 load times
        if (this.metrics.loadTimes.length > 50) {
            this.metrics.loadTimes.shift();
        }
        
        // Check for slow loads
        if (loadTime > this.thresholds.slowLoad) {
            console.warn(`⚠️ Slow resource load: ${resource} (${loadTime}ms)`);
        }
    }
    
    /**
     * Record error for performance analysis
     * @param {Error} error - Error object
     * @param {string} context - Error context
     */
    recordError(error, context = '') {
        const errorRecord = {
            message: error.message,
            stack: error.stack,
            context,
            timestamp: Date.now(),
            isMobile: MobileDetectionService.isMobile()
        };
        
        this.metrics.errors.push(errorRecord);
        
        // Keep only last 100 errors
        if (this.metrics.errors.length > 100) {
            this.metrics.errors.shift();
        }
        
        console.error('🚨 Error recorded for performance analysis:', error.message, context);
    }
    
    /**
     * Get performance summary
     * @returns {Object} - Performance summary
     */
    getPerformanceSummary() {
        const now = Date.now();
        const last24Hours = now - (24 * 60 * 60 * 1000);
        
        // Filter recent metrics
        const recentTransitions = this.metrics.sceneTransitions.filter(t => t.timestamp > last24Hours);
        const recentInteractions = this.metrics.userInteractions.filter(i => i.timestamp > last24Hours);
        const recentErrors = this.metrics.errors.filter(e => e.timestamp > last24Hours);
        
        // Calculate averages
        const avgTransitionTime = recentTransitions.length > 0 
            ? recentTransitions.reduce((sum, t) => sum + t.duration, 0) / recentTransitions.length 
            : 0;
            
        const avgInteractionTime = recentInteractions.length > 0
            ? recentInteractions.reduce((sum, i) => sum + i.responseTime, 0) / recentInteractions.length
            : 0;
        
        const errorRate = recentInteractions.length > 0
            ? recentErrors.length / recentInteractions.length
            : 0;
        
        return {
            transitions: {
                count: recentTransitions.length,
                averageTime: Math.round(avgTransitionTime),
                slowCount: recentTransitions.filter(t => t.duration > this.thresholds.slowTransition).length
            },
            interactions: {
                count: recentInteractions.length,
                averageResponseTime: Math.round(avgInteractionTime),
                slowCount: recentInteractions.filter(i => i.responseTime > 500).length
            },
            errors: {
                count: recentErrors.length,
                rate: Math.round(errorRate * 100) / 100
            },
            memoryUsage: this._getCurrentMemoryUsage(),
            recommendations: this._getPerformanceRecommendations()
        };
    }
    
    /**
     * Initialize performance monitoring
     * @private
     */
    _initializeMonitoring() {
        // Monitor page load performance
        if (document.readyState === 'complete') {
            this._recordPageLoadMetrics();
        } else {
            window.addEventListener('load', () => {
                this._recordPageLoadMetrics();
            });
        }
        
        // Monitor unhandled errors
        window.addEventListener('error', (event) => {
            this.recordError(event.error || new Error(event.message), 'Global error handler');
        });
        
        // Monitor unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.recordError(new Error(event.reason), 'Unhandled promise rejection');
        });
    }
    
    /**
     * Setup interaction monitoring
     * @private
     */
    _setupInteractionMonitoring() {
        let interactionStart = 0;
        
        // Monitor click interactions
        document.addEventListener('click', (e) => {
            interactionStart = performance.now();
            
            // Record response time after a brief delay
            setTimeout(() => {
                const responseTime = performance.now() - interactionStart;
                const target = e.target.closest('[data-action], [data-game], .nav-item, button');
                const action = target ? (target.dataset.action || target.dataset.game || target.className) : 'unknown';
                
                this.recordInteraction('click', responseTime, action);
            }, 10);
        });
    }
    
    /**
     * Setup transition monitoring
     * @private
     */
    _setupTransitionMonitoring() {
        // This will be integrated with NavigationActionHandler
        // Performance recording is already implemented there
    }
    
    /**
     * Collect memory metrics
     * @private
     */
    _collectMemoryMetrics() {
        if (performance.memory) {
            const memory = {
                used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
                total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
                limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024),
                timestamp: Date.now()
            };
            
            this.metrics.memoryUsage.push(memory);
            
            // Keep only last 100 memory readings
            if (this.metrics.memoryUsage.length > 100) {
                this.metrics.memoryUsage.shift();
            }
            
            // Check for memory issues
            if (memory.used > this.thresholds.highMemoryUsage) {
                console.warn(`⚠️ High memory usage detected: ${memory.used}MB`);
            }
        }
    }
    
    /**
     * Get current memory usage
     * @private
     */
    _getCurrentMemoryUsage() {
        if (performance.memory) {
            return {
                used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
                total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
                percentage: Math.round((performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100)
            };
        }
        return null;
    }
    
    /**
     * Analyze performance trends
     * @private
     */
    _analyzePerformanceTrends() {
        const summary = this.getPerformanceSummary();
        
        // Log performance summary
        console.log('📊 Performance Summary (24h):', {
            avgTransitionTime: `${summary.transitions.averageTime}ms`,
            avgInteractionTime: `${summary.interactions.averageResponseTime}ms`,
            errorRate: `${summary.errors.rate * 100}%`,
            memoryUsage: summary.memoryUsage ? `${summary.memoryUsage.used}MB` : 'N/A'
        });
        
        // Auto-optimization recommendations
        if (summary.transitions.averageTime > 800) {
            console.warn('🔧 Performance recommendation: Consider optimizing scene transitions');
        }
        
        if (summary.interactions.averageResponseTime > 300) {
            console.warn('🔧 Performance recommendation: Consider optimizing user interaction handlers');
        }
    }
    
    /**
     * Get performance recommendations
     * @private
     */
    _getPerformanceRecommendations() {
        const recommendations = [];
        
        // FIXED: Avoid infinite recursion by calculating metrics directly
        const now = Date.now();
        const last24Hours = now - (24 * 60 * 60 * 1000);
        
        const recentTransitions = this.metrics.sceneTransitions.filter(t => t.timestamp > last24Hours);
        const recentInteractions = this.metrics.userInteractions.filter(i => i.timestamp > last24Hours);
        const recentErrors = this.metrics.errors.filter(e => e.timestamp > last24Hours);
        
        const avgTransitionTime = recentTransitions.length > 0 
            ? recentTransitions.reduce((sum, t) => sum + t.duration, 0) / recentTransitions.length 
            : 0;
            
        const avgInteractionTime = recentInteractions.length > 0
            ? recentInteractions.reduce((sum, i) => sum + i.responseTime, 0) / recentInteractions.length
            : 0;
        
        const errorRate = recentInteractions.length > 0
            ? recentErrors.length / recentInteractions.length
            : 0;
        
        if (avgTransitionTime > 800) {
            recommendations.push('Optimize scene transition performance');
        }
        
        if (avgInteractionTime > 300) {
            recommendations.push('Optimize user interaction response times');
        }
        
        if (errorRate > this.thresholds.errorRate) {
            recommendations.push('Investigate and fix recurring errors');
        }
        
        const memoryUsage = this._getCurrentMemoryUsage();
        if (memoryUsage && memoryUsage.percentage > 80) {
            recommendations.push('Optimize memory usage and cleanup');
        }
        
        return recommendations;
    }
    
    /**
     * Record page load metrics
     * @private
     */
    _recordPageLoadMetrics() {
        if (performance.timing) {
            const timing = performance.timing;
            const loadTime = timing.loadEventEnd - timing.navigationStart;
            const domReady = timing.domContentLoadedEventEnd - timing.navigationStart;
            
            console.log('📊 Page load metrics:', {
                totalLoadTime: `${loadTime}ms`,
                domReadyTime: `${domReady}ms`
            });
            
            this.recordLoadTime('page', loadTime);
        }
    }
    
    /**
     * Export performance data
     * @returns {Object} - Complete performance data
     */
    exportPerformanceData() {
        return {
            summary: this.getPerformanceSummary(),
            rawMetrics: this.metrics,
            deviceInfo: {
                isMobile: MobileDetectionService.isMobile(),
                userAgent: navigator.userAgent,
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight
                },
                connection: navigator.connection ? {
                    effectiveType: navigator.connection.effectiveType,
                    downlink: navigator.connection.downlink
                } : null
            },
            timestamp: Date.now()
        };
    }
    
    /**
     * Show performance dashboard (for debugging)
     */
    showPerformanceDashboard() {
        const summary = this.getPerformanceSummary();
        
        console.group('📊 Performance Dashboard');
        console.log('🚀 Scene Transitions:', summary.transitions);
        console.log('👆 User Interactions:', summary.interactions);
        console.log('🚨 Errors:', summary.errors);
        console.log('💾 Memory Usage:', summary.memoryUsage);
        console.log('💡 Recommendations:', summary.recommendations);
        console.groupEnd();
        
        // Show in notification if there are issues
        if (summary.recommendations.length > 0) {
            professionalNotifications.warning(
                `Performance issues detected. Check console for details.`,
                { duration: 5000 }
            );
        } else {
            professionalNotifications.success(
                `Performance is optimal! All metrics within targets.`,
                { duration: 3000 }
            );
        }
    }
    
    /**
     * Get transition performance for specific route
     * @param {string} fromScene - Source scene
     * @param {string} toScene - Target scene
     * @returns {Object} - Route performance data
     */
    getRoutePerformance(fromScene, toScene) {
        const routeTransitions = this.metrics.sceneTransitions.filter(
            t => t.fromScene === fromScene && t.toScene === toScene
        );
        
        if (routeTransitions.length === 0) {
            return null;
        }
        
        const avgDuration = routeTransitions.reduce((sum, t) => sum + t.duration, 0) / routeTransitions.length;
        const successRate = routeTransitions.filter(t => t.success).length / routeTransitions.length;
        
        return {
            count: routeTransitions.length,
            averageDuration: Math.round(avgDuration),
            successRate: Math.round(successRate * 100) / 100,
            lastTransition: routeTransitions[routeTransitions.length - 1]
        };
    }
    
    /**
     * Clear all performance data
     */
    clearMetrics() {
        console.log('🔄 PerformanceMonitoringSystem: Clearing all metrics');
        
        this.metrics = {
            sceneTransitions: [],
            memoryUsage: [],
            userInteractions: [],
            loadTimes: [],
            errors: []
        };
        
        console.log('✅ Performance metrics cleared');
    }
    
    /**
     * Check if performance is optimal
     * @returns {boolean} - Whether performance meets enterprise standards
     */
    isPerformanceOptimal() {
        const summary = this.getPerformanceSummary();
        
        return (
            summary.transitions.averageTime < 800 &&
            summary.interactions.averageResponseTime < 300 &&
            summary.errors.rate < this.thresholds.errorRate &&
            (!summary.memoryUsage || summary.memoryUsage.percentage < 80)
        );
    }
}

// Export singleton instance and make globally available for debugging
export const performanceMonitoring = PerformanceMonitoringSystem.getInstance();

// Global access for debugging
if (typeof window !== 'undefined') {
    window.performanceMonitoring = performanceMonitoring;
    window.showPerformanceDashboard = () => performanceMonitoring.showPerformanceDashboard();
}

export default performanceMonitoring;
